import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Clock, DollarSign, Zap, ArrowRight, Building2, CalendarDays } from "lucide-react";

/* ------------------------------------------------------------------ */
/*  ShiftCard — premium reusable card for the marketplace             */
/*  variants: 'list' (default) | 'compact' | 'feature'                */
/* ------------------------------------------------------------------ */

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtMoney(n) {
  if (n === null || n === undefined) return "—";
  return `$${Number(n).toFixed(0)}/hr`;
}

export default function ShiftCard({ shift, variant = "list", onClaim, claimLabel = "Claim shift", index = 0 }) {
  const href = onClaim ? null : `/shifts/login?intent=claim&shift=${shift.id}`;
  const isFeature = variant === "feature";
  const isCompact = variant === "compact";
  const urgent = !!shift.urgent;

  return (
    <motion.article
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.3, delay: Math.min(index * 0.03, 0.18) }}
      className={`group relative rounded-3xl bg-white border border-slate-200 transition-all hover:-translate-y-1 hover:shadow-card-hover ${
        isFeature ? "p-7 shadow-navy" : "p-5 shadow-card"
      } ${urgent ? "ring-1 ring-trital-red/20" : ""}`}
      data-testid={`shift-card-${shift.id}`}
    >
      {/* Top row — facility + urgency badge */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy text-[10px] font-bold uppercase tracking-wider">
            {shift.specialty || "Per diem"}
          </div>
          <div className="mt-2.5 inline-flex items-center gap-1.5 text-slate-700 font-display font-bold text-[15px]">
            <Building2 className="w-3.5 h-3.5 text-slate-400" strokeWidth={1.8} />
            <span className="truncate">{shift.facility_name || "TRITAL Care Network Facility"}</span>
          </div>
        </div>
        {urgent && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-trital-red text-white text-[10px] font-bold uppercase tracking-wider whitespace-nowrap" data-testid={`shift-urgent-${shift.id}`}>
            <Zap className="w-3 h-3" strokeWidth={2} /> Urgent
          </span>
        )}
      </div>

      {/* Title — shift type + duration */}
      <div className={`mt-3 font-display font-black text-slate-900 leading-tight ${isFeature ? "text-2xl" : "text-[20px]"}`}>
        {shift.shift_type || "Shift"}
        {shift.duration_hours ? <span className="text-slate-400 font-bold"> · {shift.duration_hours}h</span> : null}
      </div>

      {/* Meta row — location + start time */}
      <div className="mt-3 grid grid-cols-2 gap-2.5 text-[12.5px] text-slate-600">
        <div className="inline-flex items-start gap-1.5 min-w-0">
          <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" strokeWidth={1.8} />
          <span className="truncate">{shift.city ? `${shift.city}, ${shift.state}` : (shift.state || "—")}</span>
        </div>
        <div className="inline-flex items-start gap-1.5 min-w-0">
          <CalendarDays className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" strokeWidth={1.8} />
          <span className="truncate">{fmtTime(shift.start_time)}</span>
        </div>
      </div>

      {/* Notes (only on feature variant) */}
      {isFeature && shift.notes && (
        <p className="mt-3 text-[13px] text-slate-600 leading-relaxed line-clamp-2">{shift.notes}</p>
      )}

      {/* Bottom row — pay + CTA */}
      <div className={`mt-5 pt-4 border-t border-slate-100 flex items-center justify-between gap-3 ${isCompact ? "flex-col items-stretch" : ""}`}>
        <div className="flex flex-col">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Pay rate</div>
          <div className="font-display font-black text-trital-navy text-2xl tabular-nums leading-none mt-1">
            <DollarSign className="inline w-4 h-4 -mt-1 text-trital-red" strokeWidth={2.4} />{Number(shift.hourly_rate || 0).toFixed(0)}
            <span className="text-slate-400 text-sm font-bold">/hr</span>
          </div>
        </div>
        {onClaim ? (
          <button
            type="button"
            onClick={() => onClaim(shift)}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-trital-navy text-white text-[13px] font-semibold btn-lift hover:bg-trital-navy-deep group-hover:bg-trital-red transition-colors"
            data-testid={`shift-claim-${shift.id}`}
          >
            {claimLabel} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
          </button>
        ) : (
          <Link
            to={href}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-trital-navy text-white text-[13px] font-semibold btn-lift hover:bg-trital-navy-deep group-hover:bg-trital-red transition-colors"
            data-testid={`shift-claim-${shift.id}`}
          >
            Sign in to claim <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
          </Link>
        )}
      </div>
    </motion.article>
  );
}

/* Skeleton loader for shift cards (used in BrowseShifts) */
export function ShiftCardSkeleton({ index = 0 }) {
  return (
    <div className="rounded-3xl bg-white border border-slate-200 shadow-card p-5 animate-pulse" style={{ animationDelay: `${index * 60}ms` }}>
      <div className="h-4 w-24 rounded-full bg-slate-200" />
      <div className="mt-3 h-5 w-40 rounded bg-slate-200" />
      <div className="mt-3 h-7 w-32 rounded bg-slate-100" />
      <div className="mt-4 grid grid-cols-2 gap-2">
        <div className="h-4 rounded bg-slate-100" />
        <div className="h-4 rounded bg-slate-100" />
      </div>
      <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
        <div className="h-7 w-20 rounded bg-slate-100" />
        <div className="h-9 w-28 rounded-full bg-slate-100" />
      </div>
    </div>
  );
}
